import { jsPDF } from "jspdf";
import {
  appendHistogramHierarchyParams,
  createHistogramDefaultDateFilter,
  EMPTY_HIERARCHY_FILTERS,
  formatHistogramBucketTooltip,
  getHistogramDefaultRange,
  histogramEntityLabel,
  HISTOGRAM_BUCKET_OPTIONS,
  resolveHistogramDateFilterRange,
} from "./dashboardHistogramChartUtils";
import {
  buildHistogramPdfChartImages,
  buildRainfallAnalysisText,
  buildWaterLevelAnalysisText,
} from "./dashboardHistogramPdfCharts";
import { DISPLAY_DATETIME_FORMAT, parseUtcInstant } from "./dateTimeUtils";
import { SHOW_RAINFALL } from "../Constants/featureFlags";
import { formatCmWithMeters } from "./lidarTelemetryFields";

export const HISTOGRAM_REPORT_TITLE = "Waterlogging Telemetry Report";

const formatMetric = (value) => {
  if (value === 0 || value === "0") return "0";
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return String(num);
};

const formatReportInstant = (value) => {
  if (!value) return "—";
  const parsed = parseUtcInstant(value);
  return parsed ? parsed.local().format(DISPLAY_DATETIME_FORMAT) : String(value);
};

const slugify = (value) =>
  String(value ?? "All-Entities")
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48) || "All-Entities";

const formatFilenameDate = (value) => {
  const parsed = parseUtcInstant(value);
  return parsed ? parsed.local().format("YYYY-MM-DD") : "unknown-date";
};

export const getHistogramIntervalLabel = (bucket) =>
  HISTOGRAM_BUCKET_OPTIONS.find((option) => option.value === bucket)?.label ??
  bucket ??
  "—";

export function buildHistogramExportFilename(meta, extension = "pdf") {
  const entitySlug = slugify(meta?.entityName);
  const fromPart = formatFilenameDate(meta?.from);
  const toPart = formatFilenameDate(meta?.to);
  return `Waterlogging_Telemetry_Report_${entitySlug}_${fromPart}_to_${toPart}.${extension}`;
}

export const getDefaultHistogramExportRange = () => {
  const resolved = resolveHistogramDateFilterRange(
    createHistogramDefaultDateFilter(),
  );
  return resolved ?? getHistogramDefaultRange();
};

export const buildHistogramExportParams = ({
  filterQuery,
  hierarchyFilters,
  bucket,
  groupBy,
  entityId,
  from,
  to,
}) => {
  const params = appendHistogramHierarchyParams(
    filterQuery,
    hierarchyFilters ?? EMPTY_HIERARCHY_FILTERS,
  );
  params.set("bucket", bucket);
  params.set("groupBy", groupBy);
  if (entityId) params.set("entityId", entityId);
  params.set("from", from.toISOString());
  params.set("to", to.toISOString());
  params.set("includeRainfall", String(SHOW_RAINFALL));
  return Object.fromEntries(params.entries());
};

export const validateHistogramExportRange = (from, to) => {
  if (!from || !to) {
    return "Select both from and to dates for export.";
  }
  if (from >= to) {
    return "From date must be before to date.";
  }
  return "";
};

function getPeriodLabel(row, bucket) {
  if (row.periodLabel) return row.periodLabel;
  return formatHistogramBucketTooltip(row.time, bucket);
}

export async function downloadHistogramPdf(payload) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const meta = payload?.meta ?? {};
  const summary = payload?.summary ?? {};
  const rows = payload?.rows ?? [];
  const bucket = meta.bucket ?? "1h";

  const reportTitle = meta.reportTitle ?? HISTOGRAM_REPORT_TITLE;
  const groupByLabel = meta.groupByLabel ?? histogramEntityLabel(meta.groupBy ?? "zone");
  const intervalLabel = meta.intervalLabel ?? getHistogramIntervalLabel(bucket);
  const generatedAt = meta.generatedAt ?? new Date().toISOString();
  const entityLabel = `${groupByLabel}: ${meta.entityName ?? "All"}`;

  const chartImages = await buildHistogramPdfChartImages(rows, bucket, {
    confirmedDistPeakCm: summary.confirmedDistPeakCm ?? null,
  });
  const rainfallAnalysis = SHOW_RAINFALL
    ? buildRainfallAnalysisText(rows, summary, meta)
    : "";
  const waterLevelAnalysis = buildWaterLevelAnalysisText(rows, summary, meta);

  const ensureSpace = (neededMm) => {
    if (y + neededMm > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  const addLine = (text, size = 10, bold = false, indent = 0) => {
    ensureSpace(size * 0.45 + 2.5);
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.text(String(text), margin + indent, y);
    y += size * 0.45 + 2.5;
  };

  const addKeyValue = (label, value, boldValue = false) => {
    ensureSpace(5);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(`${label}:`, margin, y);
    doc.setFont("helvetica", boldValue ? "bold" : "normal");
    doc.text(String(value), margin + 52, y);
    y += 5;
  };

  const addWrappedParagraph = (text, size = 9, lineHeight = 4.3) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(size);
    const lines = doc.splitTextToSize(String(text), contentWidth);
    lines.forEach((line) => {
      ensureSpace(lineHeight);
      doc.text(line, margin, y);
      y += lineHeight;
    });
    y += 2;
  };

  const addChartSection = (title, subtitle, imageDataUrl, analysisText) => {
    ensureSpace(24);
    addLine(title, 12, true);
    addLine(subtitle, 9);
    y += 1;

    if (imageDataUrl) {
      const chartHeightMm = 72;
      ensureSpace(chartHeightMm + 6);
      doc.addImage(imageDataUrl, "PNG", margin, y, contentWidth, chartHeightMm);
      y += chartHeightMm + 5;
    } else {
      addWrappedParagraph("No chart data available for this metric in the selected period.");
    }

    addLine("Detailed analysis", 10, true);
    addWrappedParagraph(analysisText);
    y += 4;
  };

  addLine(reportTitle, 16, true);
  y += 2;
  addLine("Report Parameters", 11, true);
  addKeyValue("Entity", entityLabel);
  addKeyValue("Reporting Period Start", formatReportInstant(meta.from));
  addKeyValue("Reporting Period End", formatReportInstant(meta.to));
  addKeyValue("Aggregation Interval", intervalLabel);

  y += 2;
  addLine("Period Summary", 11, true);
  addKeyValue(
    "Peak Water Level",
    formatCmWithMeters(summary.waterLevelAvgCm, { empty: "—" }) ?? "—",
    true,
  );
  if (SHOW_RAINFALL) {
    addKeyValue("Total Rainfall (mm)", formatMetric(summary.rainfallTotalMm), true);
  }

  y += 4;
  addChartSection(
    SHOW_RAINFALL ? "Rainfall & water level" : "Water level",
    `${entityLabel} · ${intervalLabel} · ${SHOW_RAINFALL ? "combined interval chart" : "interval chart"}`,
    chartImages.combined,
    SHOW_RAINFALL ? `${rainfallAnalysis}\n\n${waterLevelAnalysis}` : waterLevelAnalysis,
  );

  ensureSpace(16);
  addLine("Interval Data", 11, true);

  const colPeriod = margin;
  const colRainfall = margin + 72;
  const colWl = SHOW_RAINFALL ? margin + 120 : margin + 72;
  // const colCumulative = margin + 150;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("Period", colPeriod, y);
  if (SHOW_RAINFALL) {
    doc.text("Rainfall (mm)", colRainfall, y);
  }
  doc.text("Water Level (cm / m)", colWl, y);
  // doc.text("Cumulative (mm)", colCumulative, y);
  y += 4.5;

  doc.setDrawColor(200, 200, 200);
  doc.line(margin, y, pageWidth - margin, y);
  y += 3;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);

  if (!rows.length) {
    addLine("No telemetry data available for the selected filters.");
  } else {
    rows.forEach((row) => {
      if (y > pageHeight - margin - 8) {
        doc.addPage();
        y = margin;
      }
      const period = getPeriodLabel(row, bucket);
      doc.text(
        period.length > 34 ? `${period.slice(0, 31)}...` : period,
        colPeriod,
        y,
      );
      if (SHOW_RAINFALL) {
        doc.text(formatMetric(row.rainfallMm), colRainfall, y);
      }
      doc.text(
        formatCmWithMeters(row.waterLevelAvgCm, { empty: "—" }) ?? "—",
        colWl,
        y,
      );
      // doc.text(formatMetric(row.cumulativeRainfallMm), colCumulative, y);
      y += 4.2;
    });
  }

  const footerY = pageHeight - 8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(
    `Generated on ${formatReportInstant(generatedAt)}`,
    margin,
    footerY,
  );
  doc.setTextColor(0, 0, 0);

  doc.save(buildHistogramExportFilename(meta, "pdf"));
}
